import { useSession } from '@/auth/SessionProvider';
import { useStickerPackExecutionById } from '@/hooks/useStickerPack';
import { SpinnerFullPage } from '@/components/ui';
import { StickerPackVisualization } from '@/components/StickerPack/StickerPackVisualization';
import { Link } from '@tanstack/react-router';
import type { Content } from '@/types/content';
import { useRevealContent } from '@/hooks/useContents';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export function StickerList({
  selectedContents,
}: {
  selectedContents: Content[];
}) {
  const { session } = useSession();
  const queryClient = useQueryClient();
  const [isRevealing, setIsRevealing] = useState(false);
  const revealMutation = useRevealContent(session);

  // Check if any content in the pack is unrevealed
  const hasUnrevealed = selectedContents.some(c => c.revealedAt === null);

  const {
    data: execution,
    isLoading,
    error,
  } = useStickerPackExecutionById(selectedContents?.[0].executionId!, session);

  const handleReveal = async () => {
    if (isRevealing) return;

    setIsRevealing(true);
    try {
      // Get all unrevealed content IDs
      const unrevealedIds = selectedContents
        .filter(c => c.revealedAt === null)
        .map(c => c.id);

      // Reveal all content in parallel
      await Promise.all(
        unrevealedIds.map(contentId => revealMutation.mutateAsync(contentId))
      );

      // Final invalidation after all reveals complete
      queryClient.invalidateQueries({
        queryKey: ['content'],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['sticker-pack-execution', selectedContents[0].executionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['sticker-pack-executions'],
      });
    } catch (error) {
      console.error('Failed to reveal content:', error);
    } finally {
      setIsRevealing(false);
    }
  };

  if (isLoading) {
    return <SpinnerFullPage text="Loading your sticker packs..." />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-red-600">
          Error loading sticker packs: {error.message}
        </div>
      </div>
    );
  }

  if (!execution) {
    return (
      <div className="flex flex-col items-center justify-center">
        <div className="text-tg-hint text-center text-lg">
          No sticker packs yet
        </div>
        <p className="text-tg-hint mt-2 text-center text-sm">
          Create your first sticker pack to see it here!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Executions List */}
      {execution && (
        <div className="space-y-2">
          <div key={execution.id} className="overflow-hidden rounded-xl">
            {/* Header */}
            <div className="flex items-start justify-between gap-2 pt-4">
              <div className="min-w-0 flex-1">
                <Link
                  to="/sticker-packs/generated/$id"
                  params={{ id: execution.id.toString() }}
                >
                  <h3 className="text-tg-text text-base leading-tight font-semibold">
                    {execution.bundle.name}
                    {execution.effect_style !== 'basic' &&
                      ` (${execution.effect_style.toUpperCase()})`}
                  </h3>
                </Link>
                {execution.nft_token && (
                  <div className="text-tg-hint mt-0.5 truncate text-xs">
                    {execution.nft_token.contract.name} #
                    {execution.nft_token.id}
                  </div>
                )}
              </div>
              <span
                className={`flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
                  execution.status === 'completed'
                    ? 'bg-green-100 text-green-700'
                    : execution.status === 'processing'
                      ? 'bg-blue-100 text-blue-700'
                      : execution.status === 'failed'
                        ? 'bg-red-100 text-red-700'
                        : execution.status === 'pending_payment'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-700'
                }`}
              >
                {execution.completed_prompts}/{execution.total_prompts}
              </span>
            </div>

            {/* Sticker Grid Visualization */}
            <div className="relative py-2">
              <div className={hasUnrevealed ? 'blur-sm' : ''}>
                <StickerPackVisualization execution={execution} />
              </div>

              {/* Reveal Button Overlay */}
              {hasUnrevealed && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <button
                    onClick={handleReveal}
                    disabled={isRevealing}
                    className="bg-tg-button text-tg-button-text hover:bg-tg-button/90 flex items-center gap-2 rounded-full px-6 py-3 font-semibold shadow-lg transition-all active:scale-95 disabled:opacity-50"
                  >
                    {isRevealing ? (
                      <>
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        <span>Revealing...</span>
                      </>
                    ) : (
                      'Reveal'
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Actions */}
            {!hasUnrevealed && (
              <div className="flex flex-col gap-2 pb-4">
                {/* Telegram Link for completed */}
                {execution.status === 'completed' &&
                  execution.telegram_pack_url && (
                    <a
                      href={execution.telegram_pack_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-tg-button text-tg-button-text block rounded-lg py-2.5 text-center text-sm font-semibold transition-colors"
                    >
                      Add to Telegram
                    </a>
                  )}

                {/* Regenerate Link */}
                <Link
                  to="/sticker-packs/generated/$id"
                  params={{ id: execution.id.toString() }}
                  className="border-tg-button text-tg-button hover:bg-tg-button/10 block rounded-lg border-2 py-2.5 text-center text-sm font-semibold transition-colors"
                >
                  Regenerate
                </Link>
              </div>
            )}

            {/* Error Message */}
            {execution.status === 'failed' && execution.error_message && (
              <div className="mx-4 mb-4 rounded-lg bg-red-50 p-3">
                <p className="text-xs text-red-700">
                  {execution.error_message}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
